import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import Button from "../Button";
import Icon from "../Icon";
import SecondaryAction from "./SecondaryAction";
import {
  bannerPrimaryButtonStyle,
  variantAccentColors,
  variantSurfaceClassNames,
  variantChipClassNames,
  variantIconClassNames,
  variantIcons
} from "./shared";
import type { BannerProps } from "./shared";
import "./Banner.css";

function InternalNavButton({ to, children, ...props }: { to: string; children: React.ReactNode; [x: string]: any }) {
  const navigate = useNavigate();
  return (
    <Button {...props} onClick={() => navigate(to)}>
      {children}
    </Button>
  );
}

function ViewportCopy({ full, compact }: { full: string; compact?: string }) {
  if (!compact) {
    return full;
  }

  return (
    <>
      <span className="sm:hidden">{compact}</span>
      <span className="hidden sm:inline">{full}</span>
    </>
  );
}

export default function Banner({
  description,
  compactDescription,
  secondaryAction,
  primaryAction,
  variant = "info",
  closable,
  onClose,
  inline,
  layout = "contained",
  subText,
  testId = "banner",
  className,
  hideSecondaryDismiss = false
}: BannerProps) {
  const [isVisible, setIsVisible] = useState<boolean>(true);

  if (!isVisible) {
    return null;
  }

  const handleDismiss = () => {
    if (onClose) {
      onClose();
      return;
    }
    setIsVisible(false);
  };

  const handlePrimaryAction = () => {
    if (primaryAction?.onClick) {
      primaryAction.onClick();
      return;
    }
    if (primaryAction?.link) {
      window.open(primaryAction.link, "_blank");
      return;
    }
    // If primary action doesn't have a link or onClick, it should not auto-dismiss
    // Dismiss functionality should be explicit via closable, onClose, or secondary action
  };

  // Auto-add dismiss secondary action if primaryAction exists and no secondaryAction is provided
  const effectiveSecondaryAction =
    secondaryAction || (primaryAction && !hideSecondaryDismiss ? { label: "Dismiss", isDismiss: true } : undefined);

  const hasSecondaryDismiss = effectiveSecondaryAction?.isDismiss;
  const hasInternalAction = primaryAction?.link && primaryAction.isInternal;

  // Only show primary dismiss button if no secondary dismiss and (closable or onClose without primary action)
  const showPrimaryDismiss = !hasSecondaryDismiss && (closable || (onClose && !primaryAction));

  // Check if there are any actions to render
  const hasActions = effectiveSecondaryAction || primaryAction || showPrimaryDismiss;
  const keepActionsInRow = inline || layout === "full";
  const iconName = variantIcons[variant];
  const primaryButtonStyle = bannerPrimaryButtonStyle(variant);

  const paddingClassName = layout === "full" ? "px-4" : "px-3";

  return (
    <div
      className={classNames(
        "banner",
        variantSurfaceClassNames[variant],
        layout === "full" && "ring-1 ring-inset",
        layout === "header" && "border-b",
        layout === "contained" && "rounded-md border",
        className
      )}
      style={{ "--banner-accent": variantAccentColors[variant] } as CSSProperties}
      role={variant === "error" ? "alert" : "status"}
      data-layout={layout}
      data-variant={variant}
      data-testid={testId}
    >
      <div className={paddingClassName}>
        <div
          className={classNames(
            "flex items-center justify-between py-3",
            keepActionsInRow ? "flex-row gap-3" : "flex-col sm:flex-row"
          )}
        >
          <div
            className={classNames(
              "flex min-w-0 items-center gap-3",
              keepActionsInRow ? "flex-1 text-left" : "text-center sm:text-left"
            )}
          >
            <div
              className={classNames("banner__icon", variantChipClassNames[variant])}
              data-testid="banner-icon"
              aria-hidden="true"
            >
              <Icon name={iconName} size={24} filled className={variantIconClassNames[variant]} />
            </div>
            <div className="flex min-w-0 flex-col">
              <p
                className="text-title-small-bold text-sys-brand-on-surface text-left whitespace-pre-wrap"
                {...(compactDescription ? { "aria-label": description } : {})}
              >
                <ViewportCopy full={description} compact={compactDescription} />
              </p>
              {subText &&
                (Array.isArray(subText) ? (
                  subText.map((line, index) => (
                    <p key={index} className="text-label-large text-sys-brand-on-surface-variant mt-0.5 text-left">
                      {line}
                    </p>
                  ))
                ) : typeof subText === "string" ? (
                  <p className="text-label-large text-sys-brand-on-surface-variant mt-0.5 text-left">{subText}</p>
                ) : (
                  <div className="text-label-large text-sys-brand-on-surface-variant mt-0.5 text-left">{subText}</div>
                ))}
            </div>
          </div>
          {hasActions && (
            <div
              className={classNames("flex items-center space-x-4 flex-shrink-0", !keepActionsInRow && "mt-4 sm:mt-0")}
            >
              <SecondaryAction secondaryAction={effectiveSecondaryAction} onDismiss={handleDismiss} />
              {(primaryAction || showPrimaryDismiss) &&
                (hasInternalAction && primaryAction?.link && primaryAction?.label ? (
                  <InternalNavButton
                    to={primaryAction.link}
                    variant="filled"
                    style={primaryButtonStyle}
                    ariaLabel={primaryAction.compactLabel ? primaryAction.label : undefined}
                  >
                    <ViewportCopy full={primaryAction.label} compact={primaryAction.compactLabel} />
                  </InternalNavButton>
                ) : (
                  <Button
                    variant="filled"
                    style={primaryButtonStyle}
                    onClick={showPrimaryDismiss ? handleDismiss : handlePrimaryAction}
                    type="button"
                    ariaLabel={primaryAction?.compactLabel ? primaryAction.label : undefined}
                  >
                    {primaryAction ? (
                      <ViewportCopy full={primaryAction.label} compact={primaryAction.compactLabel} />
                    ) : (
                      "Dismiss"
                    )}
                  </Button>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
